/**
 * Registry view — the month-grouped list of cases below the chart.
 *
 * Groups by `Case.folder` (e.g., `"June 2026"`); when the folder is missing
 * or does not parse as an en-US "Month YYYY" label, the case falls into an
 * `"Unfiled"` group rendered after every valid month group. Within each
 * group, cases sort by `dateISO` descending with case-insensitive `title`
 * ascending as the tiebreak; cases whose `dateISO` is empty or
 * non-parseable sort last within their group.
 *
 * `businessUnit` and `owner` are shown when non-null.
 *
 * Mode-gating: edit and delete controls are rendered only when the view is
 * mounted with `mode: "authoring"`. In `"published"` mode the row exposes
 * no mutation affordance — this is the DOM half of the read-only guarantee
 * (Requirement 17). Delete requires an affirmative `confirm()` before the
 * `onDelete` callback fires; cancel leaves state untouched.
 *
 * DOM structure mirrors the prototype (`ai-maturity-index-portal_9.html`):
 *
 *   <section class="registry">
 *     <div class="folder" data-folder="June 2026">
 *       <div class="folder-bar">
 *         <span class="fname">June 2026</span>
 *         <span class="fmeta">3 cases</span>
 *       </div>
 *       <div class="folder-body">
 *         <div class="row" data-case-id="…">
 *           <div class="id">#00001</div>
 *           <div class="title">
 *             <div class="name">Title…</div>
 *             <div class="meta">Business Unit · Owner</div>
 *           </div>
 *           <div class="actions">
 *             <span class="pill prod|dev|pipe">Status</span>
 *             <button class="edit">Edit</button>       ← authoring only
 *             <button class="delete">Delete</button>   ← authoring only
 *           </div>
 *         </div>
 *       </div>
 *     </div>
 *   </section>
 *
 * The portal shell (task 7.7) drives `render()` when `Case[]` changes and
 * supplies `onEdit`/`onDelete` callbacks that translate to `PATCH`/`DELETE`
 * requests through `ApiClient`.
 *
 * Requirements: 13.1, 13.2, 13.3, 13.4, 13.5, 13.6, 13.7, 13.8, 13.9.
 */
import type { Case, CaseStatus } from "@aim/types";

/** Options accepted by `mount`. */
export interface RegistryViewOptions {
  /** Boot mode; controls whether edit/delete controls are exposed. */
  mode: "authoring" | "published";
  /** Invoked when the edit control on a case is activated (authoring only). */
  onEdit?: (caseId: string) => void;
  /**
   * Invoked when the delete control on a case is activated AND the analyst
   * affirmatively confirms the destructive action (authoring only).
   */
  onDelete?: (caseId: string) => void;
}

/** Public surface of the registry view. */
export interface RegistryView {
  /** Mount the registry shell into `container`. Idempotent — safe to call twice. */
  mount(container: HTMLElement, opts: RegistryViewOptions): void;
  /** Render (or re-render) the current case list. Safe to call before mount is a no-op. */
  render(cases: readonly Case[]): void;
  /** Unmount and release cached element references. */
  destroy(): void;
}

/** Label used for the fallback group of cases with unparseable/missing folders. */
export const UNFILED_LABEL = "Unfiled";

/** en-US month name → 0-based month index. Case-insensitive lookup. */
const MONTH_INDEX: ReadonlyMap<string, number> = new Map([
  ["january", 0],
  ["february", 1],
  ["march", 2],
  ["april", 3],
  ["may", 4],
  ["june", 5],
  ["july", 6],
  ["august", 7],
  ["september", 8],
  ["october", 9],
  ["november", 10],
  ["december", 11],
]);

interface ParsedFolder {
  year: number;
  month: number;
}

/**
 * Parse an en-US `"Month YYYY"` folder label into `{ year, month }`.
 * Returns `null` when the label is missing, empty, or does not match the
 * expected shape. Extracted as a pure function so tests can exercise the
 * folder-parsing edge cases without touching the DOM.
 */
export function parseFolderLabel(folder: string): ParsedFolder | null {
  const trimmed = folder.trim();
  if (trimmed.length === 0) return null;

  const parts = trimmed.split(/\s+/);
  if (parts.length !== 2) return null;

  const monthName = parts[0];
  const yearStr = parts[1];
  if (monthName === undefined || yearStr === undefined) return null;
  if (!/^\d{4}$/.test(yearStr)) return null;

  const month = MONTH_INDEX.get(monthName.toLowerCase());
  if (month === undefined) return null;

  const year = Number.parseInt(yearStr, 10);
  return { year, month };
}

/**
 * Parse an ISO date string to epoch-ms. Returns `null` when the input is
 * empty or `Date.parse` yields `NaN`. Kept local — the domain layer owns
 * canonical date handling.
 */
function parseCaseDate(dateISO: string): number | null {
  if (dateISO.trim().length === 0) return null;
  const t = Date.parse(dateISO);
  return Number.isFinite(t) ? t : null;
}

/** Case-insensitive lexicographic comparator on `title`. */
function compareTitleCaseInsensitive(a: string, b: string): number {
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (al < bl) return -1;
  if (al > bl) return 1;
  return 0;
}

/**
 * Case comparator used inside a folder group:
 *   - dateISO descending (parseable dates first),
 *   - title case-insensitive ascending as tiebreak,
 *   - cases with null/invalid dateISO sort last within the group.
 */
function compareCasesWithinGroup(a: Case, b: Case): number {
  const da = parseCaseDate(a.dateISO);
  const db = parseCaseDate(b.dateISO);
  if (da === null && db === null) {
    return compareTitleCaseInsensitive(a.title, b.title);
  }
  if (da === null) return 1;
  if (db === null) return -1;
  if (da !== db) return db - da; // date desc
  return compareTitleCaseInsensitive(a.title, b.title);
}

/** A group of cases produced by `computeGroups`. */
export interface RegistryGroup {
  /** Display label — either the original folder or `"Unfiled"`. */
  label: string;
  /** Parsed folder anchor; `null` for the unfiled fallback group. */
  parsed: ParsedFolder | null;
  /** Cases sorted per `compareCasesWithinGroup`. */
  cases: readonly Case[];
}

/**
 * Group `cases` by `folder`, sort groups (year+month descending, unfiled
 * last), and sort cases within each group. Pure — extracted so the tests
 * can hit ordering directly.
 */
export function computeGroups(cases: readonly Case[]): readonly RegistryGroup[] {
  const byLabel = new Map<
    string,
    { label: string; parsed: ParsedFolder | null; cases: Case[] }
  >();

  for (const c of cases) {
    const parsed = parseFolderLabel(c.folder);
    const label = parsed !== null ? c.folder : UNFILED_LABEL;
    let group = byLabel.get(label);
    if (group === undefined) {
      group = { label, parsed, cases: [] };
      byLabel.set(label, group);
    }
    group.cases.push(c);
  }

  const groups = Array.from(byLabel.values());

  // Group ordering: valid groups by (year desc, month desc); unfiled last.
  groups.sort((a, b) => {
    if (a.parsed === null && b.parsed === null) return 0;
    if (a.parsed === null) return 1;
    if (b.parsed === null) return -1;
    if (a.parsed.year !== b.parsed.year) return b.parsed.year - a.parsed.year;
    return b.parsed.month - a.parsed.month;
  });

  for (const g of groups) g.cases.sort(compareCasesWithinGroup);

  return groups;
}

/** Map a `CaseStatus` to the pill CSS variant. */
function pillClass(status: CaseStatus): "prod" | "dev" | "pipe" {
  switch (status) {
    case "Production":
      return "prod";
    case "Development":
      return "dev";
    case "Pipeline":
      return "pipe";
  }
}

/** `#00001`-style display id for the case's sequential number. */
function formatCaseNum(num: number): string {
  return `#${String(num).padStart(5, "0")}`;
}

/**
 * Build the metadata line (`businessUnit · owner`). Fields with a null or
 * whitespace-only value are omitted; when both are empty the meta line is
 * not rendered at all (returned as `null`).
 */
function buildMetaLine(c: Case): HTMLElement | null {
  const bits: string[] = [];
  if (c.businessUnit !== null && c.businessUnit.trim().length > 0) {
    bits.push(c.businessUnit);
  }
  if (c.owner !== null && c.owner.trim().length > 0) {
    bits.push(c.owner);
  }
  if (bits.length === 0) return null;

  const meta = document.createElement("div");
  meta.className = "meta";
  meta.textContent = bits.join(" · ");
  return meta;
}

/** Build one `.row` element for a single case. */
function buildRow(c: Case, opts: RegistryViewOptions): HTMLElement {
  const row = document.createElement("div");
  row.className = "row";
  row.dataset.caseId = c.id;

  const idBox = document.createElement("div");
  idBox.className = "id";
  idBox.textContent = formatCaseNum(c.num);

  const titleBox = document.createElement("div");
  titleBox.className = "title";
  const name = document.createElement("div");
  name.className = "name";
  name.textContent = c.title;
  titleBox.append(name);

  const meta = buildMetaLine(c);
  if (meta !== null) titleBox.append(meta);

  const actions = document.createElement("div");
  actions.className = "actions";

  const pill = document.createElement("span");
  pill.className = `pill ${pillClass(c.status)}`;
  pill.textContent = c.status;
  actions.append(pill);

  // Requirement 13.7 / 13.9: edit and delete controls only in authoring mode.
  // The absence of these buttons in published mode is what keeps the DOM
  // free of mutation affordances (Requirement 17).
  if (opts.mode === "authoring") {
    const editBtn = document.createElement("button");
    editBtn.type = "button";
    editBtn.className = "edit";
    editBtn.textContent = "Edit";
    editBtn.setAttribute("aria-label", `Edit ${c.title}`);
    editBtn.addEventListener("click", () => {
      opts.onEdit?.(c.id);
    });
    actions.append(editBtn);

    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "delete";
    deleteBtn.textContent = "Delete";
    deleteBtn.setAttribute("aria-label", `Delete ${c.title}`);
    deleteBtn.addEventListener("click", () => {
      // Requirement 13.8: destructive action requires an explicit affirmative
      // confirmation. `confirm()` returns `false` on cancel, which leaves
      // state unchanged. A richer modal is out of scope for task 7.5 and
      // will land alongside the confirm-modal work.
      const affirmed = globalThis.confirm(
        `Delete ${formatCaseNum(c.num)} — "${c.title}"? This also removes the stored deck.`,
      );
      if (affirmed) opts.onDelete?.(c.id);
    });
    actions.append(deleteBtn);
  }

  row.append(idBox, titleBox, actions);
  return row;
}

/** Build one `.folder` element for a group. */
function buildFolder(
  group: RegistryGroup,
  opts: RegistryViewOptions,
): HTMLElement {
  const folder = document.createElement("div");
  folder.className = "folder";
  folder.dataset.folder = group.label;

  const bar = document.createElement("div");
  bar.className = "folder-bar";

  const fname = document.createElement("span");
  fname.className = "fname";
  fname.textContent = group.label;

  const fmeta = document.createElement("span");
  fmeta.className = "fmeta";
  const count = group.cases.length;
  fmeta.textContent = `${String(count)} case${count === 1 ? "" : "s"}`;

  bar.append(fname, fmeta);

  const body = document.createElement("div");
  body.className = "folder-body";
  for (const c of group.cases) body.append(buildRow(c, opts));

  folder.append(bar, body);
  return folder;
}

/** Build the empty-state placeholder shown when the portfolio is empty. */
function buildEmptyState(): HTMLElement {
  const empty = document.createElement("div");
  empty.className = "empty";
  const heading = document.createElement("h3");
  heading.textContent = "No use cases yet";
  const body = document.createElement("p");
  body.textContent =
    "Upload a use case deck to number it, file it by month, and start plotting the AI Maturity Index.";
  empty.append(heading, body);
  return empty;
}

/** Factory — every call returns an independent view instance. */
export function createRegistryView(): RegistryView {
  let root: HTMLElement | null = null;
  let options: RegistryViewOptions | null = null;

  return {
    mount(container: HTMLElement, opts: RegistryViewOptions): void {
      // Idempotent: replace any previous contents with a fresh `.registry`
      // section. The portal shell mounts once per boot so this is mostly
      // defensive, but it lets tests re-mount cheaply.
      container.replaceChildren();
      root = document.createElement("section");
      root.className = "registry";
      options = opts;
      container.append(root);
    },

    render(cases: readonly Case[]): void {
      if (root === null || options === null) {
        // Rendering before mount is a no-op; the shell will pick up state
        // when mounted next. Callers (the portal shell) always mount first.
        return;
      }

      if (cases.length === 0) {
        root.replaceChildren(buildEmptyState());
        return;
      }

      // Bind `options` to a local const so TS keeps the narrowing inside
      // the `map` callback below.
      const opts = options;
      const groups = computeGroups(cases);
      const nodes = groups.map((g) => buildFolder(g, opts));
      root.replaceChildren(...nodes);
    },

    destroy(): void {
      if (root !== null) {
        root.remove();
        root = null;
      }
      options = null;
    },
  };
}
